import { useState, useCallback } from "react";
import Layout from "../../layout/Layout";
import useModificationRequests from "./hooks/useModificationRequests";
import Loading from "../../common/Loading";
import "bootstrap-daterangepicker/daterangepicker.css";
import Header from "./components/Header";
import CustomerInformation from "./components/CustomerInformation";
import ModificationDetails from "./components/ModificationDetails";
import CancellationFeeInformation from "./components/CancellationFeeInformation";
import PaymentDetails from "./components/PaymentDetails";
import ApproveConfirmationModal from "./components/ApproveConfirmationModal";
import SuccessNotification from "./components/SuccessNotification";
import { Link } from "react-router-dom";
import { LinkIcon } from "../../icons";
import customAxios from "../../../utils/customAxios";

const Page = ({ match, history }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [processingAction, setProcessingAction] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const queryParams = {
    id: match.params.id,
    limit: 1,
    refreshKey,
  };

  const { data, isLoadingRows } = useModificationRequests(queryParams);

  const isDraft = data?.status === "DRAFT";

  const handleApproveDraft = useCallback(async () => {
    setProcessingAction("approved");
    try {
      await customAxios.post("/api/admin/request-change-booking/approve-draft", {
        requestId: data?._id,
      });
      setShowApproveModal(false);
      setSuccessMessage("Booking modification approved and updated successfully.");
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("Approve draft failed:", err);
    } finally {
      setProcessingAction(null);
    }
  }, [data?._id]);

  return (
    <Layout>
      <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 tw:pb-[42px] w-100" >
        <div className="tw:font-hanken-grotesk tw:text-[#212529]">
          {isLoadingRows ? (
            <Loading />
          ) : (
            <>
              <Header data={data} />

              <CustomerInformation booking={data.bookingId} />

              {data?.requestType === "reschedule" && (
                <ModificationDetails data={data} />
              )}

              <div className="d-flex align-items-start gap-5 tw:!pb-6">
                <Link
                  to={`/admin/bookinglist/edit/v2/${data?.bookingId?._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw:inline-flex tw:items-center tw:gap-[5px] tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:px-3 tw:py-2 tw:text-[#282828] tw:!no-underline"
                >
                  <span className="tw:!font-hanken-grotesk tw:text-[#282828] tw:!font-[400] tw:text-[14px]">View Booking Details</span>
                  <LinkIcon color="#282828" size={8} />
                </Link>

                {isDraft && (
                  <button
                    type="button"
                    onClick={() => setShowApproveModal(true)}
                    className="tw:inline-flex tw:items-center tw:gap-[5px] tw:rounded-[5px] tw:bg-[#3CAEA3] tw:px-5 tw:py-2 tw:border-0"
                  >
                    <span className="tw:!font-hanken-grotesk tw:text-white tw:!font-[600] tw:text-[14px]">
                      {data?.chargeAmount > 0
                        ? `¥ Approve & Charge ${data.chargeAmount.toLocaleString()} JPY`
                        : "Approve Draft"}
                    </span>
                  </button>
                )}
              </div>

              {(data?.totalPriceCancellation > 0 || data?.totalMeetAssistPriceCancellation > 0) && (
                <CancellationFeeInformation
                  data={data}
                  meetAssistChangesIdentification={data?.changesIdentification?.meetAssist}
                />
              )}

              <PaymentDetails
                data={data}
                isDeferredPayment={data?.userId?.deferredPaymentEligible}
              />
            </>
          )}
        </div>

        {showApproveModal && (
          <ApproveConfirmationModal
            data={data}
            isDeferredPayment={data?.userId?.deferredPaymentEligible}
            processingAction={processingAction}
            onClose={() => setShowApproveModal(false)}
            onConfirm={handleApproveDraft}
          />
        )}

        {successMessage && (
          <SuccessNotification
            message={successMessage}
            onClose={() => setSuccessMessage("")}
          />
        )}
      </main>
    </Layout>
  );
};

export default Page;